import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useI18n } from '../../context/I18nContext';
import { useReportData } from '../../context/ReportDataContext';
import { Skeleton } from '../../components/ui/skeleton';
import './ScoreSimulator.css';
import {
  buildReportIntelligence,
  buildSuggestedGoals,
  formatReportDate,
  getBureauDisplayName,
  getScoreBandKey,
  GOAL_STORAGE_KEY,
  MANUAL_DEMO,
  SIMULATION_ACTIONS,
  TIMEFRAME_OPTIONS,
} from './scoreSimulatorLogic';

const SS = 'creditBuilder.scoreSimulator';

const SIMULATION_CTA_KEYS = {
  payDownUtilization: 'openPaymentPlanner',
  increaseCreditLimit: 'openCliPlanner',
  addAuthorizedUser: 'openAuBlueprint',
  addPrimaryAccount: 'openTradelineStrategy',
  resolveCollection: 'openDisputeWizard',
  addRentReporting: 'openRentReporting',
  avoidNewInquiries: 'viewStrategy',
};

const ScoreSimulator = () => {
  const { t, locale } = useI18n();
  const { reportData, loading: reportLoading, hasReport, refreshData } = useReportData();

  const [manualMode, setManualMode] = useState(false);
  const [selectedBureau, setSelectedBureau] = useState('experian');
  const [goalScore, setGoalScore] = useState('');
  const [timeframeKey, setTimeframeKey] = useState('365');
  const [planGenerated, setPlanGenerated] = useState(false);
  const [savedGoal, setSavedGoal] = useState(null);
  const [activeGoalCard, setActiveGoalCard] = useState(12);

  const intelligence = useMemo(() => {
    if (manualMode && !hasReport) {
      const goals = buildSuggestedGoals(MANUAL_DEMO.currentScore);
      const best = goals.find((g) => g.bestFit) || goals[3];
      return {
        ...MANUAL_DEMO,
        snapshots: [
          { id: 'experian', name: 'Experian', score: MANUAL_DEMO.currentScore, available: true },
          { id: 'equifax', name: 'Equifax', score: null, available: false },
          { id: 'transunion', name: 'TransUnion', score: null, available: false },
        ],
        suggestedGoal: best.score,
        goalGap: best.delta,
        positiveCount: 3,
        negativeCount: 3,
        topPositiveKey: 'noMissedPayments',
        topNegativeKey: 'heavyRevolvingUsage',
        factors: [
          { key: 'noMissedPayments', sentiment: 'positive', impact: 'high', ctaRoute: '/credit-report/analysis', ctaKey: 'viewReport' },
          { key: 'heavyRevolvingUsage', sentiment: 'negative', impact: 'high', ctaRoute: '/credit-builder/payment-planner', ctaKey: 'paymentPlanner' },
          { key: 'shortCreditHistory', sentiment: 'negative', impact: 'medium', ctaRoute: '/blueprints/authorized-user', ctaKey: 'auBlueprint' },
        ],
        suggestedGoals: goals,
        actionPlan: [
          { key: 'payDownUtilization', impactMin: 10, impactMax: 35, confidence: 'high', timeMin: 30, timeMax: 60, route: '/credit-builder/payment-planner', ctaKey: 'paymentPlanner' },
          { key: 'increaseLimits', impactMin: 5, impactMax: 25, confidence: 'medium', timeMin: 30, timeMax: 90, route: '/credit-builder/cli-planner', ctaKey: 'cliPlanner' },
        ],
        reportDate: null,
        scoreModel: { label: null, isFico: false },
      };
    }
    if (!reportData) return null;
    return buildReportIntelligence(reportData);
  }, [manualMode, hasReport, reportData]);

  useEffect(() => {
    if (!intelligence) return;
    setSelectedBureau(intelligence.primaryBureau || 'experian');
    setGoalScore(String(intelligence.suggestedGoal || ''));
    const best = intelligence.suggestedGoals?.find((g) => g.bestFit);
    if (best) setActiveGoalCard(best.months);
  }, [intelligence?.primaryBureau, intelligence?.suggestedGoal]);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(GOAL_STORAGE_KEY);
      if (raw) setSavedGoal(JSON.parse(raw));
    } catch {
      /* ignore */
    }
  }, []);

  const tf = useCallback((key, params, fallback) => t(`${SS}.${key}`, params, fallback), [t]);

  const factorText = useCallback(
    (factor, field) => {
      if (!factor?.key) return '';
      return tf(`factors.items.${factor.key}.${field}`);
    },
    [tf]
  );

  const factorCtaLabel = useCallback(
    (ctaKey) => tf(`factors.cta.${ctaKey}`),
    [tf]
  );

  const bureauSnapshot = useMemo(() => {
    if (!intelligence?.snapshots) return null;
    return intelligence.snapshots.find((b) => b.id === selectedBureau) || intelligence.snapshots[0];
  }, [intelligence, selectedBureau]);

  const currentScore = intelligence?.currentScore ?? 0;
  const displayGoal = Number(goalScore) || intelligence?.suggestedGoal || 0;
  const gap = Math.max(0, displayGoal - (currentScore || 0));
  const months = Math.round(Number(TIMEFRAME_OPTIONS.find((o) => o.key === timeframeKey)?.days || 365) / 30);

  const scoreBandLabel = (score) => {
    const key = getScoreBandKey(score);
    return tf(`bureau.bands.${key}`);
  };

  const summaryNotePositive = () => {
    if (intelligence?.topPositiveKey) {
      return factorText({ key: intelligence.topPositiveKey }, 'title');
    }
    return tf('summary.paymentHistoryStrong');
  };

  const summaryNoteNegative = () => {
    if (intelligence?.topNegativeKey) {
      return factorText({ key: intelligence.topNegativeKey }, 'title');
    }
    return tf('summary.utilizationNeedsAttention');
  };

  const handleGeneratePlan = () => {
    setPlanGenerated(true);
    const payload = {
      currentScore,
      goalScore: displayGoal,
      timeframeKey,
      bureau: selectedBureau,
      savedAt: new Date().toISOString(),
      nextActionKey: intelligence?.actionPlan?.[0]?.key || 'payDownUtilization',
    };
    localStorage.setItem(GOAL_STORAGE_KEY, JSON.stringify(payload));
    setSavedGoal(payload);
  };

  const handleSelectSuggestedGoal = (goal) => {
    setGoalScore(String(goal.score));
    setActiveGoalCard(goal.months);
    const tfMatch = TIMEFRAME_OPTIONS.find((o) => Math.round(o.days / 30) === goal.months);
    if (tfMatch) setTimeframeKey(tfMatch.key);
  };

  const firstPlanRoute = intelligence?.actionPlan?.[0]?.route || '/credit-builder/payment-planner';
  const firstPlanCtaKey = intelligence?.actionPlan?.[0]?.ctaKey || 'paymentPlanner';
  const timeframeLabel = tf(`goals.timeframes.${timeframeKey}`);

  const modelDisplay = () => {
    const m = intelligence?.scoreModel;
    if (!m?.label) return tf('bureau.modelNotSpecified');
    if (m.isFico) return m.label;
    return m.label;
  };

  const localeTag = locale === 'es' ? 'es-ES' : locale === 'fr' ? 'fr-FR' : 'en-US';
  const reportDateFormatted = formatReportDate(
    bureauSnapshot?.reportDate || intelligence?.reportDate,
    localeTag
  );

  if (reportLoading && !manualMode) {
    return (
      <div className="score-goal-planner px-3 sm:px-4 lg:px-0" data-testid="score-goal-planner-loading">
        <div className="sgp-shell space-y-4">
          <Skeleton className="h-48 w-full rounded-3xl" />
          <Skeleton className="h-64 w-full rounded-3xl" />
        </div>
      </div>
    );
  }

  if (!hasReport && !manualMode) {
    return (
      <section className="score-goal-planner px-3 sm:px-4 lg:px-0" data-testid="score-goal-planner-empty">
        <div className="sgp-shell">
          <div className="sgp-card">
            <div className="sgp-empty">
              <h3>{tf('empty.title')}</h3>
              <p>{tf('empty.body')}</p>
              <div className="sgp-empty-actions">
                <Link to="/credit-report/import" className="sgp-btn sgp-btn-primary">
                  {tf('empty.importReport')}
                </Link>
                <button
                  type="button"
                  className="sgp-btn sgp-btn-secondary"
                  onClick={() => setManualMode(true)}
                >
                  {tf('empty.manualEstimate')}
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="score-goal-planner px-3 sm:px-4 lg:px-0" data-testid="score-goal-planner">
      <div className="sgp-shell">
        {manualMode && !hasReport && (
          <div className="sgp-demo-banner">{tf('manual.banner')}</div>
        )}

        <div className="sgp-hero">
          <div className="sgp-panel">
            <div className="sgp-content">
              <div className="sgp-eyebrow">
                <span className="sgp-dot" />
                {tf('hero.eyebrow')}
              </div>
              <h1 className="sgp-title">{tf('hero.title')}</h1>
              <p className="sgp-subtitle">{tf('hero.subtitle')}</p>
              <div className="sgp-disclaimer">
                <strong>{tf('hero.disclaimerLabel')}</strong> {tf('hero.disclaimerBody')}
              </div>
            </div>
          </div>

          <div className="sgp-panel">
            <div className="sgp-content sgp-report-grid">
              <div className="sgp-stat">
                <div>
                  <div className="sgp-stat-label">{tf('summary.currentScore')}</div>
                  <div className="sgp-stat-value">{currentScore || '—'}</div>
                </div>
                <div className="sgp-stat-note">
                  {manualMode && !hasReport
                    ? tf('summary.manualEstimate')
                    : tf('summary.importedFromReport', {
                        bureau: getBureauDisplayName(intelligence?.primaryBureau),
                      })}
                </div>
              </div>
              <div className="sgp-stat highlight">
                <div>
                  <div className="sgp-stat-label">{tf('summary.suggestedGoal')}</div>
                  <div className="sgp-stat-value">{displayGoal || '—'}</div>
                </div>
                <div className="sgp-stat-note">{tf('summary.estimatedGap', { points: gap })}</div>
              </div>
              <div className="sgp-stat">
                <div>
                  <div className="sgp-stat-label">{tf('summary.positiveFactors')}</div>
                  <div className="sgp-stat-value">{intelligence?.positiveCount ?? 0}</div>
                </div>
                <div className="sgp-stat-note">{summaryNotePositive()}</div>
              </div>
              <div className="sgp-stat">
                <div>
                  <div className="sgp-stat-label">{tf('summary.negativeFactors')}</div>
                  <div className="sgp-stat-value">{intelligence?.negativeCount ?? 0}</div>
                </div>
                <div className="sgp-stat-note">{summaryNoteNegative()}</div>
              </div>
            </div>
          </div>
        </div>

        <div className="sgp-card">
          <div className="sgp-section-head">
            <div>
              <h2 className="sgp-section-title">{tf('bureau.title')}</h2>
              <p className="sgp-section-copy">{tf('bureau.subtitle')}</p>
            </div>
            <div className="sgp-factor-summary">
              <span className="sgp-pill good">
                {tf('bureau.positiveCount', { count: intelligence?.positiveCount ?? 0 })}
              </span>
              <span className="sgp-pill bad">
                {tf('bureau.negativeCount', { count: intelligence?.negativeCount ?? 0 })}
              </span>
              {reportDateFormatted && (
                <span className="sgp-pill">{tf('bureau.reportDate', { date: reportDateFormatted })}</span>
              )}
            </div>
          </div>

          <div className="sgp-bureau-tabs">
            {intelligence?.snapshots?.map((b) => (
              <button
                key={b.id}
                type="button"
                className={`sgp-bureau-tab ${selectedBureau === b.id ? 'active' : ''} ${!b.available ? 'disabled' : ''}`}
                disabled={!b.available}
                onClick={() => b.available && setSelectedBureau(b.id)}
              >
                {b.name}
              </button>
            ))}
          </div>

          <div className="sgp-score-layout">
            <div className="sgp-score-card">
              {bureauSnapshot?.available ? (
                <>
                  <div className="sgp-score-label">{tf('bureau.scoreFromReport')}</div>
                  <div className="sgp-score-value">{bureauSnapshot.score}</div>
                  <div className="sgp-score-band">{scoreBandLabel(bureauSnapshot.score)}</div>
                  <div className="sgp-score-source">
                    {tf('bureau.source')}: {tf('bureau.sourceValue', { source: bureauSnapshot.name })}
                    <br />
                    {tf('bureau.model')}: {modelDisplay()}
                  </div>
                </>
              ) : (
                <p className="sgp-section-copy">{tf('bureau.noBureauData')}</p>
              )}
            </div>
            <div className="sgp-trend">
              <div className="sgp-trend-head">
                <div>
                  <h3 className="sgp-section-title" style={{ fontSize: '20px' }}>
                    {tf('bureau.scoreTrend')}
                  </h3>
                  <p className="sgp-section-copy">{tf('bureau.trendHint')}</p>
                </div>
              </div>
              <div className="sgp-trend-placeholder">{tf('bureau.noTrendData')}</div>
            </div>
          </div>
        </div>

        <div className="sgp-card">
          <div className="sgp-section-head">
            <div>
              <h2 className="sgp-section-title">{tf('factors.title')}</h2>
              <p className="sgp-section-copy">{tf('factors.subtitle')}</p>
            </div>
          </div>
          <div className="sgp-factor-list">
            {(intelligence?.factors || []).map((factor) => (
              <div key={factor.key} className="sgp-factor-row">
                <div className={`sgp-factor-icon ${factor.sentiment === 'positive' ? 'up' : 'down'}`}>
                  {factor.sentiment === 'positive' ? '↑' : '↓'}
                </div>
                <div>
                  <div className="sgp-factor-title">{factorText(factor, 'title')}</div>
                  <div className="sgp-factor-copy">{factorText(factor, 'description')}</div>
                  <div className="sgp-factor-meta">
                    <span className={`sgp-pill ${factor.sentiment === 'positive' ? 'good' : 'bad'}`}>
                      {tf(`factors.sentiment.${factor.sentiment}`)}
                    </span>
                    <span className="sgp-pill">{tf(`factors.impact.${factor.impact}`)}</span>
                    <span className="sgp-pill">{factorText(factor, 'actionLabel')}</span>
                  </div>
                </div>
                <Link to={factor.ctaRoute} className="sgp-btn sgp-btn-secondary sgp-btn-small">
                  {factorCtaLabel(factor.ctaKey)}
                </Link>
              </div>
            ))}
          </div>
        </div>

        <div className="sgp-grid-2">
          <div className="sgp-card">
            <div className="sgp-section-head">
              <div>
                <h2 className="sgp-section-title">{tf('goals.buildTitle')}</h2>
                <p className="sgp-section-copy">{tf('goals.buildSubtitle')}</p>
              </div>
            </div>
            <form
              className="sgp-form"
              onSubmit={(e) => {
                e.preventDefault();
                handleGeneratePlan();
              }}
            >
              <div className="sgp-field">
                <label>
                  {tf('goals.currentScore')} <span>{tf('goals.fromLatestReport')}</span>
                </label>
                <input className="sgp-input" type="number" value={currentScore} readOnly />
              </div>
              <div className="sgp-field">
                <label>
                  {tf('goals.goalScore')} <span>{tf('goals.target')}</span>
                </label>
                <input
                  className="sgp-input"
                  type="number"
                  min={currentScore}
                  max={850}
                  value={goalScore}
                  onChange={(e) => setGoalScore(e.target.value)}
                />
              </div>
              <div className="sgp-field">
                <label>
                  {tf('goals.timeFrame')} <span>{tf('goals.chooseOne')}</span>
                </label>
                <select
                  className="sgp-select"
                  value={timeframeKey}
                  onChange={(e) => setTimeframeKey(e.target.value)}
                >
                  {TIMEFRAME_OPTIONS.map((opt) => (
                    <option key={opt.key} value={opt.key}>
                      {tf(`goals.timeframes.${opt.key}`)}
                    </option>
                  ))}
                </select>
              </div>
              <button type="submit" className="sgp-btn sgp-btn-primary">
                {tf('goals.generatePlan')}
              </button>
            </form>
          </div>

          <div className="sgp-card">
            <div className="sgp-section-head">
              <div>
                <h2 className="sgp-section-title">{tf('goals.suggestedTitle')}</h2>
                <p className="sgp-section-copy">{tf('goals.suggestedSubtitle')}</p>
              </div>
            </div>
            <div className="sgp-goals">
              {(intelligence?.suggestedGoals || []).map((goal) => (
                <button
                  key={goal.months}
                  type="button"
                  className={`sgp-goal-card ${activeGoalCard === goal.months ? 'active' : ''}`}
                  onClick={() => handleSelectSuggestedGoal(goal)}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <div>
                      <div className="sgp-goal-score">{goal.score}</div>
                      <div className="sgp-goal-time">{tf('goals.withinMonths', { months: goal.months })}</div>
                    </div>
                    <span className="sgp-gap">{tf('goals.pointsGain', { points: goal.delta })}</span>
                  </div>
                  <div className="sgp-badge-row">
                    <span className="sgp-pill good">{tf(`goals.difficulty.${goal.difficulty}`)}</span>
                    <span className="sgp-pill">{tf(`goals.confidence.${goal.confidence}`)}</span>
                    {goal.bestFit && <span className="sgp-pill good">{tf('goals.difficulty.bestFit')}</span>}
                    {goal.reportBased && <span className="sgp-pill">{tf('goals.reportBased')}</span>}
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>

        {(planGenerated || savedGoal) && (
          <div className="sgp-card">
            <div className="sgp-section-head">
              <div>
                <h2 className="sgp-section-title">
                  {months >= 12 ? tf('plan.titleMonths', { months }) : tf('plan.title')}
                </h2>
                <p className="sgp-section-copy">{tf('plan.subtitle')}</p>
              </div>
              <Link to={firstPlanRoute} className="sgp-btn sgp-btn-primary sgp-btn-small">
                {tf('plan.startFirstAction')}
              </Link>
            </div>
            <div className="sgp-plan">
              {(intelligence?.actionPlan || []).map((item, idx) => (
                <div key={item.key} className="sgp-plan-item">
                  <div className="sgp-step-num">{idx + 1}</div>
                  <div>
                    <div className="sgp-plan-title">{tf(`plan.items.${item.key}.title`)}</div>
                    <div className="sgp-plan-copy">{tf(`plan.items.${item.key}.description`)}</div>
                    <div className="sgp-badge-row">
                      {item.protects ? (
                        <span className="sgp-pill">{tf('plan.protectsScore')}</span>
                      ) : (
                        <span className="sgp-pill good">
                          {tf('plan.estimatedImpact', {
                            min: `+${item.impactMin}`,
                            max: `+${item.impactMax}`,
                          })}
                        </span>
                      )}
                      <span className="sgp-pill">{tf(`goals.confidence.${item.confidence}`)}</span>
                      <span className="sgp-pill">
                        {item.timeMin === 0 && item.timeMax <= 30
                          ? tf('plan.immediate')
                          : tf('plan.timeToImpact', { min: item.timeMin, max: item.timeMax })}
                      </span>
                    </div>
                  </div>
                  <Link to={item.route} className="sgp-btn sgp-btn-secondary sgp-btn-small">
                    {tf(`plan.cta.${item.ctaKey}`)}
                  </Link>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="sgp-card">
          <div className="sgp-section-head">
            <div>
              <h2 className="sgp-section-title">{tf('simulations.title')}</h2>
              <p className="sgp-section-copy">{tf('simulations.subtitle')}</p>
            </div>
          </div>
          <div className="sgp-actions">
            {SIMULATION_ACTIONS.map((action) => (
              <div key={action.key} className="sgp-action">
                <div>
                  <h3 className="sgp-action-title">{tf(`simulations.items.${action.key}.title`)}</h3>
                  <p className="sgp-action-copy">{tf(`simulations.items.${action.key}.description`)}</p>
                </div>
                <div className="sgp-action-meta">
                  <div>
                    <div className="sgp-mini-label">{tf('simulations.impact')}</div>
                    <div className="sgp-mini-value">{tf(`simulations.items.${action.key}.impact`)}</div>
                  </div>
                  <div>
                    <div className="sgp-mini-label">{tf('simulations.confidence')}</div>
                    <div className="sgp-mini-value">{tf(`simulations.items.${action.key}.confidence`)}</div>
                  </div>
                  <div>
                    <div className="sgp-mini-label">{tf('simulations.tool')}</div>
                    <div className="sgp-mini-value">{tf(`simulations.tools.${action.toolKey}`)}</div>
                  </div>
                </div>
                <Link to={action.route} className="sgp-btn sgp-btn-primary sgp-btn-small">
                  {tf(`simulations.${SIMULATION_CTA_KEYS[action.key]}`)}
                </Link>
              </div>
            ))}
          </div>
        </div>

        {(planGenerated || savedGoal) && (
          <div className="sgp-card">
            <div className="sgp-confirm">
              <div>
                <h3 className="sgp-section-title">{tf('confirmation.title')}</h3>
                <p className="sgp-section-copy">{tf('confirmation.body')}</p>
                <div className="sgp-badge-row" style={{ marginTop: 12 }}>
                  <span className="sgp-pill good">{tf('confirmation.current', { score: currentScore })}</span>
                  <span className="sgp-pill">{tf('confirmation.goal', { score: displayGoal })}</span>
                  <span className="sgp-pill">{tf('confirmation.target', { timeframe: timeframeLabel })}</span>
                  <span className="sgp-pill good">
                    {tf('confirmation.nextAction', { action: tf(`plan.cta.${firstPlanCtaKey}`) })}
                  </span>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                <button type="button" className="sgp-btn sgp-btn-secondary" onClick={() => window.scrollTo({ top: 0 })}>
                  {tf('confirmation.viewGoalDetails')}
                </button>
                <Link to={firstPlanRoute} className="sgp-btn sgp-btn-primary">
                  {tf('plan.startFirstAction')}
                </Link>
              </div>
            </div>
          </div>
        )}

        {hasReport && (
          <div className="sgp-card" style={{ textAlign: 'center' }}>
            <button
              type="button"
              className="sgp-btn sgp-btn-secondary sgp-btn-small"
              onClick={() => refreshData(true)}
            >
              {tf('refresh')}
            </button>
          </div>
        )}
      </div>
    </section>
  );
};

export default ScoreSimulator;
